import React, { useState } from 'react';
import { Table, Button, Space, Modal, Tag, Tooltip, Popconfirm, Badge, Card, Alert, Input, Select, Row, Col, Form, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CheckCircleOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useOferte } from '../hooks/useOferte';
import OfertaForm from './OfertaForm';
import { useNavigate, Link } from 'react-router-dom';
import { useProdusToOfertaStatus } from '../hooks/useProdusToOfertaStatus';
import { useManagers } from '../hooks/useManagers';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { PERMISSIONS, ROLES } from '../../../../../config/permissions';

const OferteTab = () => {

  const { productStatusOptions } = useProdusToOfertaStatus();
  const { managers } = useManagers();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOferta, setEditingOferta] = useState(null);
  const [ messageError, setMessageError] = useState(null);
  const [filterForm] = Form.useForm();
  const navigate = useNavigate();
  const { checkPermission, getUserRole } = usePermissions();
  const { oferte, loading, pagination, fetchOferte, createOferta, updateOferta, deleteOferta, statusOptions, filters, applyFilters, clearFilters, updatePagination } = useOferte();

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.id === status);
    return statusOption ? statusOption.color : '#6B7280';
  };

  const getStatusText = (status) => {
    const statusOption = statusOptions.find(option => option.id === status);
    return statusOption ? statusOption.label : status;
  };

  // Funcție pentru a verifica dacă oferta are produse noi
  const hasNewProducts = (record) => {
    return record.info?.new_products && record.info.new_products.length > 0;
  };

  // Funcție pentru a obține informații despre status dintr-un produs
  const getProductStatusInfo = (statusId) => {
    const statusOption = productStatusOptions.find(option => option.id === statusId);
    return statusOption || { id: statusId, label: `Status ${statusId}`, color: '#8c8c8c' };
  };

  // Funcție pentru a grupa produsele după status
  const getProductsByStatus = (produse) => {
    const statusGroups = {};
    
    produse?.forEach(produs => {
      const statusInfo = getProductStatusInfo(produs.product_status_id);
      if (!statusGroups[statusInfo.id]) {
        statusGroups[statusInfo.id] = {
          ...statusInfo,
          count: 0,
          products: []
        };
      }
      statusGroups[statusInfo.id].count++;
      statusGroups[statusInfo.id].products.push(produs);
    });
    
    return Object.values(statusGroups);
  };

  // Funcție pentru a verifica dacă oferta are produse cu status incomplet (definim statusurile complete)
  const hasIncompleteProducts = (record) => {
    // Presupunem că statusurile cu ID >= 2 sunt complete (poți ajusta în funcție de logica ta)
    return record.produse?.some(produs => produs.product_status_id < 2);
  };

  // Funcție pentru a obține prioritatea ofertei
  const getOfertaPriority = (record) => {
    const hasNew = hasNewProducts(record);
    const hasIncomplete = hasIncompleteProducts(record);
    
    if (hasNew && hasIncomplete) return 'urgent';
    if (hasNew || hasIncomplete) return 'medium';
    return 'normal';
  };

  // Funcție pentru a obține culoarea rândului bazată pe prioritate
  const getRowClassName = (record) => {
    const priority = getOfertaPriority(record);
    switch (priority) {
      case 'urgent':
        return 'urgent-row';
      case 'medium':
        return 'medium-row';
      default:
        return '';
    }
  };

  const columns = [
    {
      title: 'Ofertă',
      dataIndex: 'id',
      key: 'id',
      width: 160,
      render: (id, record) => {
        const hasNew = hasNewProducts(record);
        const hasIncomplete = hasIncompleteProducts(record);
        const priority = getOfertaPriority(record);
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Tooltip title="Vezi detalii ofertă">
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '13px',
                  height: '32px',
                  background: priority === 'urgent' 
                    ? 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)'
                    : priority === 'medium'
                    ? 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)'
                    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
                onClick={() => navigate(`${record.id}`)}
              >
                Oferta #{id}
              </Button>
            </Tooltip>

            
          </div>
        );
      },
    },
    {
      title: 'Client & Livrare',
      key: 'client_info',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
          <Link to={`/dashboard/oferte/client/${record.client_mongo_id}`}>
            <div style={{ fontWeight: '600', color: '#1890ff', marginBottom: '4px' }}>
              {record.client_name || 'Client nespecificat'}
            </div>
          </Link>
          <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
            📍 {record.shiping_city || 'Oraș nespecificat'}
          </div>
          <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {record.amo_id || 'AMO ID nespecificat'}
          </div>
        </div>
      ),
    },
    {
      title: 'Produse & Status',
      key: 'products_status',
      width: 280,
      render: (_, record) => {
        const totalProducts = (record.produse?.length || 0) + (record.info?.new_products?.length || 0);
        const newProducts = record.info?.new_products?.length || 0;
        const statusGroups = getProductsByStatus(record.produse);
        
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
              padding: '4px 8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <span style={{ fontWeight: '600' }}>Total Produse:</span>
              <span style={{ fontWeight: '600', color: '#1890ff' }}>{totalProducts}</span>
            </div>
            
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {newProducts > 0 && (
                <Tag color="red" style={{ fontSize: '11px', margin: '2px' }}>
                  🆕 {newProducts} noi
                </Tag>
              )}
              
              {statusGroups.map(statusGroup => (
                <Tag 
                  key={statusGroup.id}
                  style={{ 
                    fontSize: '11px', 
                    margin: '2px',
                    backgroundColor: statusGroup.color + '20',
                    borderColor: statusGroup.color,
                    color: statusGroup.color
                  }}
                >
                  {statusGroup.label}: {statusGroup.count}
                </Tag>
              ))}
              
              {record.produse?.length === 0 && newProducts === 0 && (
                <Tag color="default" style={{ fontSize: '11px', margin: '2px' }}>
                  Fără produse
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Situație Financiară',
      key: 'financiar',
      width: 200,
      render: (_, record) => {
        const isManager = getUserRole() === ROLES.MANAGER;
        
        const totalAchizitie = record.produse?.reduce((sum, produs) => {
          return sum + (produs.bought_price * produs.product_qt);
        }, 0) || 0;
        
        const totalVanzare = record.produse?.reduce((sum, produs) => {
          return sum + (produs.sale_price * produs.product_qt);
        }, 0) || 0;
        
        const profit = totalVanzare - totalAchizitie;
        const profitPercent = totalAchizitie > 0 ? ((profit / totalAchizitie) * 100) : 0;
        
        // Pentru utilizatorii manager, afișăm doar suma de vânzare
        if (isManager) {
          return (
            <Card 
              size="small" 
              style={{ 
                fontSize: '12px', 
                borderRadius: '8px',
                backgroundColor: '#f0f9ff',
                border: '1px solid #91d5ff'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '8px 6px',
                backgroundColor: '#1890ff',
                color: 'white',
                borderRadius: '4px',
                fontWeight: '700',
                fontSize: '11px'
              }}>
                <span>💰 TOTAL VÂNZARE:</span>
                <span>{totalVanzare.toFixed(0)} MDL</span>
              </div>
            </Card>
          );
        }
        
        // Pentru celelalte roluri (Admin, Logistic, Contabil), afișăm informațiile complete
        return (
          <Card 
            size="small" 
            style={{ 
              fontSize: '11px', 
              borderRadius: '8px',
              backgroundColor: profit >= 0 ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${profit >= 0 ? '#b7eb8f' : '#ffccc7'}`
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span style={{ color: '#ff4d4f' }}>💸 Cost:</span>
              <span style={{ fontWeight: '600' }}>{totalAchizitie.toFixed(0)} MDL</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span style={{ color: '#52c41a' }}>💰 Vânzare:</span>
              <span style={{ fontWeight: '600' }}>{totalVanzare.toFixed(0)} MDL</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '4px 6px',
              backgroundColor: profit >= 0 ? '#52c41a' : '#ff4d4f',
              color: 'white',
              borderRadius: '4px',
              fontWeight: '700',
              fontSize: '10px'
            }}>
              <span>📊 PROFIT:</span>
              <span>{profit.toFixed(0)} MDL ({profitPercent.toFixed(1)}%)</span>
            </div>
          </Card>
        );
      },
    },
    {
      title: 'Status & Data',
      key: 'status_date',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <Tag 
            color={getStatusColor(record.status)} 
            style={{ marginBottom: '8px', fontWeight: '500' }}
          >
            {getStatusText(record.status)}
          </Tag>
          <div style={{ color: '#666' }}>
            📅 {new Date(record.date_added).toLocaleDateString('ro-RO')}
          </div>
          {
            record.data_contracted && (
              <div style={{ color: '#666' }}>
                📅 {new Date(record.data_contracted).toLocaleDateString('ro-RO')}
              </div>
            )
          }
          {
            record.data_delivered && (
              <div style={{ color: '#666' }}>
                📅 {new Date(record.data_delivered).toLocaleDateString('ro-RO')}
              </div>
            )
          }
        </div>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Șterge">
            <Popconfirm
              title="Ești sigur că vrei să ștergi această ofertă?"
              onConfirm={() => deleteOferta(record.id)}
              okText="Da"
              cancelText="Nu"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginationInfo) => {
    updatePagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total
    });
  };

  const handleFormSubmit = async (values) => {
    let success = false;
    if (editingOferta) {
      success = await updateOferta(editingOferta.id, values);
    } else {
      success = await createOferta(values);
    }

    if (success) {
      setModalVisible(false);
      setEditingOferta(null);
      message.success('Ofertă creată cu succes');
    } else {
      setMessageError('Eroare la crearea ofertei');
    }
  };

  // Funcții pentru gestionarea filtrelor
  const handleFilterChange = (changedFields, allFields) => {
    const formValues = filterForm.getFieldsValue();
    applyFilters(formValues);
  };

  const handleClearFilters = () => {
    filterForm.resetFields();
    clearFilters();
  };

  // Inițializăm form-ul cu valorile curente ale filtrelor
  React.useEffect(() => {
    filterForm.setFieldsValue(filters);
  }, [filters, filterForm]);

  return (
    <div className="space-y-4">
      {/* Componenta de filtrare */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FilterOutlined style={{ color: '#1890ff' }} />
            <span>Filtrare Oferte</span>
          </div>
        }
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Form
          form={filterForm}
          layout="vertical"
          onValuesChange={handleFilterChange}
          style={{ marginBottom: 0 }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="oferta_id" 
                label="ID Ofertă"
                style={{ marginBottom: '8px' }}
              >
                <Input
                  placeholder="Caută după ID ofertă..."
                  type="number"
                  allowClear
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="search" 
                label="Căutare generală"
                style={{ marginBottom: '8px' }}
              >
                <Input
                  placeholder="nume client, oraș, AMO ID..."
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="status" 
                label="Status ofertă"
                style={{ marginBottom: '8px' }}
              >
                <Select placeholder="Toate statusurile" allowClear>
                  <Select.Option value="all">Toate statusurile</Select.Option>
                  {statusOptions.map(status => (
                    <Select.Option key={status.status} value={status.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: status.color
                          }}
                        />
                        {status.label}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="manager_id" 
                label="Manager"
                style={{ marginBottom: '8px' }}
              >
                <Select placeholder="Toți managerii" allowClear>
                  <Select.Option value="all">Toți managerii</Select.Option>
                  {managers.map(manager => (
                    <Select.Option key={manager.id} value={manager.id}>
                      {manager.prenume} {manager.nume} ({manager.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="product_status" 
                label="Status produse"
                style={{ marginBottom: '8px' }}
              >
                <Select placeholder="Toate statusurile" allowClear>
                  <Select.Option value="all">Toate statusurile</Select.Option>
                  {productStatusOptions.map(status => (
                    <Select.Option key={status.id} value={status.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: status.color
                          }}
                        />
                        {status.label}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="client_name" 
                label="Nume client"
                style={{ marginBottom: '8px' }}
              >
                <Input
                  placeholder="Caută după nume client..."
                  allowClear
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ paddingTop: '22px' }}>
                <Button 
                  type="default" 
                  icon={<ClearOutlined />} 
                  onClick={handleClearFilters}
                  style={{ marginRight: '8px' }}
                >
                  Resetează filtrele
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Alert pentru ofertele urgente */}
      {oferte.some(oferta => getOfertaPriority(oferta) === 'urgent') && (
        <Alert
          message="Atenție! Există oferte urgente care necesită atenție imediată"
          description="Ofertele marcate cu roșu au atât produse noi de prelucrat, cât și produse cu statusuri incomplete."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ borderRadius: '8px' }}
        />
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Managementul Ofertelor</h2>
          <p className="text-sm text-gray-600 mt-1">
            Monitorizează și gestionează toate ofertele din sistem
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          style={{ 
            borderRadius: '8px',
            height: '40px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
          }}
          onClick={() => {
            setEditingOferta(null);
            setModalVisible(true);
          }}
        >
          Ofertă Nouă
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={oferte}
        loading={loading}
        rowKey="id"
        rowClassName={getRowClassName}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `Afișare ${range[0]}-${range[1]} din ${total} oferte`,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        size="middle"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}
      />

      {/* Stiluri CSS pentru clasele de rând */}
      <style jsx>{`
        .urgent-row {
          background-color: #fff2f0 !important;
          border-left: 4px solid #ff4d4f !important;
        }
        .urgent-row:hover {
          background-color: #ffebe8 !important;
        }
        .medium-row {
          background-color: #fffbf0 !important;
          border-left: 4px solid #faad14 !important;
        }
        .medium-row:hover {
          background-color: #fff7e6 !important;
        }
      `}</style>

      <Modal
        title={editingOferta ? 'Editează Oferta' : 'Adaugă Ofertă Nouă'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingOferta(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <OfertaForm
          initialValues={editingOferta}
          onSubmit={handleFormSubmit}
          messageError={messageError}
          onCancel={() => {
            setModalVisible(false);
            setEditingOferta(null);
          }}
        />
      </Modal>

    </div>
  );
};

export default OferteTab; 